import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  ElementRef,
  Input,
  IterableDiffer,
  IterableDiffers,
  ViewChild,
} from '@angular/core';
import { MapOptionsShared } from '../../../models/interface/mapOptions.interface';
import { AtlasMap } from '../mu-atlas/interfaces/mu-atlas-map.interface';
import { AtlasBounds, AtlasMapElement, AtlasPosition } from '../mu-atlas/interfaces/mu-atlas-types.interface';
import { sharedMapConstants } from '../../../sharedConstants/map.contants';
import 'leaflet-fullscreen';

declare var L;

@Component({
  selector: 'app-mu-map-leaflet',
  templateUrl: './mu-map-leaflet.component.html',
  styleUrls: ['./mu-map-leaflet.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MuMapLeafletComponent implements AfterViewInit, DoCheck, AtlasMap {
  @Input() options: MapOptionsShared = sharedMapConstants.mapOptions;

  @Input() overlays: AtlasMapElement[];

  //Reference to Leaflet Map
  private map: any;

  // HTML Element to plate Leaflet Map
  @ViewChild('leafletMap') mapElementRef: ElementRef;

  differ: IterableDiffer<AtlasMapElement>;

  constructor(differs: IterableDiffers) {
    this.differ = differs.find([]).create();
  }

  ngAfterViewInit(): void {
    const center = this.options?.center
      ? [
          this.options.center.lat ? this.options.center.lat : this.options.center.latitude,
          this.options.center.lng ? this.options.center.lng : this.options.center.longitude,
        ]
      : [sharedMapConstants.mapOptions.center.lat, sharedMapConstants.mapOptions.center.lng];
    this.map = L.map(this.mapElementRef.nativeElement, {
      ...this.options,
      center,
    });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 20,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);
  }

  ngDoCheck() {
    if (this.map) {
      const changes = this.differ.diff(this.overlays);
      if (changes) {
        changes.forEachAddedItem(marker => {
          if(!marker.item.getElementForMap){
            return;
          }
          marker.item.getElementForMap().addTo(this.map);
        });

        changes.forEachRemovedItem(marker => {
          this.map.removeLayer(marker.item.getElementForMap());
        });
      }
    }
  }

  private forceRedraw(): void {
    setTimeout(() => this.map.invalidateSize());
  }

  updateOptions(options: MapOptionsShared): void {
    if (!options?.center) {
      options.center = sharedMapConstants.mapOptions.center;
    }
    const center = {
      lat: options.center.lat ? options.center.lat : options.center.latitude,
      lng: options.center.lng ? options.center.lng : options.center.longitude
    }
    this.map.setView(center, options.zoom);
    this.forceRedraw();
  }

  setCenter(center: AtlasPosition): void {
    this.map.setView(center);
  }

  getCenter(): AtlasPosition {
    return this.map.getCenter();
  }

  setZoom(zoom: number): void {
    this.map.setZoom(zoom);
  }

  panTo(position: AtlasBounds): void {
    this.map.panTo(position);
  }

  getZoom(): number {
    return this.map.getZoom();
  }

  fitBounds(bounds: AtlasBounds, padding: number = 0): void {
    if(this.map){
      const lfBounds = L.latLngBounds(bounds.getNorthEast(), bounds.getSouthWest());
      this.map.fitBounds(lfBounds, { padding: [padding + 50, padding + 100] });
      this.forceRedraw();
    }
  }

  panToBounds(bounds: AtlasBounds, padding: number = 0): void {
    if (this.map) {
      const lfBounds = L.latLngBounds(bounds.getNorthEast(), bounds.getSouthWest());
      this.map.flyToBounds(lfBounds, { padding: [padding + 50, padding + 100], duration: 0.75 });
    }
  }

  addListener(event: string, fn: (event: any) => void): void {
    const events = {
      zoom_changed: 'zoomend',
    };
    this.map.on(events[event], fn);
  }
}
